import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, MapPin, Phone, Mail, Clock, Navigation } from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import { localizeOfficeName, localizeWorkingHours } from '../../utils/helpers';
import GoogleMapPreview from '../../components/common/GoogleMapPreview';
import InstantBookingModal from '../../components/booking/InstantBookingModal';

export default function OfficesPage() {
  const { t, currentLang } = useLanguage();
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    api.get('/offices')
      .then((res) => {
        if (res?.success) setOffices(res.offices || []);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#05080e] min-h-screen text-slate-200">
      {/* Header */}
      <section className="bg-[#070c16] text-white py-14 sm:py-20 border-b border-[#141d2d] text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:underline mb-4"
          >
            &larr; {currentLang === 'uz' ? 'Bosh sahifaga qaytish' : currentLang === 'ru' ? 'Назад на главную' : 'Back to Home'}
          </Link>
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
            {t('firm_name_badge', 'JIZZAX ADVOKATLARI')} — {currentLang === 'uz' ? 'OFISLAR VA MANZILLAR' : currentLang === 'ru' ? 'ОФИСЫ И АДРЕСА' : 'OFFICES & LOCATIONS'}
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-slate-100">
            {t('nav_offices', 'Ofislarimiz va Google Xarita')}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-4 font-light leading-relaxed">
            Sizga yaqin va qulay ofisimizni tanlang hamda konsultatsiyaga yoziling.
          </p>
        </div>
      </section>

      {/* Offices list with maps */}
      <section className="py-16 sm:py-24 bg-[#05080e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {loading ? (
            <div className="text-center py-20 text-slate-400">Yuklanmoqda...</div>
          ) : (
            offices.map((office) => (
              <div
                key={office.id}
                className="bg-[#090e18] rounded-2xl border border-[#1b2b46] hover:border-[#c59b27]/60 overflow-hidden shadow-2xl hover:shadow-2xl hover:shadow-[#c59b27]/10 transition-all duration-300 p-6 sm:p-10 group relative"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                  {/* Left Col: Info */}
                  <div className="space-y-4 flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center space-x-2 px-3 py-1 rounded bg-[#111c2e] text-[#c59b27] border border-[#c59b27]/30 text-xs font-semibold mb-3">
                        <Building2 className="w-3.5 h-3.5" />
                        <span>
                          {currentLang === 'en'
                            ? (office.city === 'Jizzax' ? 'Jizzakh' : office.city) + ' Branch'
                            : currentLang === 'ru'
                            ? (office.city === 'Jizzax' ? 'Джизак' : office.city) + ' Филиал'
                            : `${office.city} Filiali`}
                        </span>
                      </div>

                      <h2 className="font-serif text-2xl font-bold text-slate-100">
                        {localizeOfficeName(office.name, currentLang)}
                      </h2>

                      <div className="space-y-3 text-xs text-slate-300 pt-4">
                        <a 
                          href={office.mapUrl || office.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${office.latitude ?? 40.160331},${office.longitude ?? 67.827246}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start hover:text-[#c59b27] transition-colors group/addr"
                          title="Google Maps xaritasida ochish"
                        >
                          <MapPin className="w-4 h-4 text-[#c59b27] mr-2.5 shrink-0 mt-0.5 group-hover/addr:scale-110 transition-transform" />
                          <span>{office.addressUz || office.address} ↗</span>
                        </a>
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-[#c59b27] mr-2.5 shrink-0" />
                          <span>{office.phone}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 text-[#c59b27] mr-2.5 shrink-0" />
                          <span>{localizeWorkingHours(office.workingHoursText || 'Dush – Shanba: 09:00 – 18:00', currentLang)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-[#141d2d] flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setBookingModalOpen(true)}
                        className="px-5 py-2.5 rounded bg-[#b38038] hover:bg-[#c59b27] text-white text-xs font-bold uppercase tracking-wider transition-colors text-center cursor-pointer"
                      >
                        {t('office_book_btn', 'Qabulga yozilish')}
                      </button>

                      {office.mapUrl && (
                        <a
                          href={office.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-5 py-2.5 rounded border border-slate-700 hover:border-slate-500 text-slate-300 text-xs font-semibold inline-flex items-center justify-center transition-colors"
                        >
                          <Navigation className="w-3.5 h-3.5 mr-1.5" />
                          {currentLang === 'en' ? 'Route' : currentLang === 'ru' ? 'Маршрут' : 'Marshrut'}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Right Col: Map */}
                  <div className="lg:col-span-2 flex flex-col">
                    <GoogleMapPreview office={office} className="flex-1 w-full h-full min-h-[380px]" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <InstantBookingModal 
        isOpen={bookingModalOpen} 
        onClose={() => setBookingModalOpen(false)} 
      />
    </div>
  );
}
